import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  BarElement,
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import {
  ArrowUpIcon,
  ArrowDownIcon,
  ClockIcon,
  ChartBarIcon,
  CurrencyDollarIcon,
  AdjustmentsHorizontalIcon,
  MagnifyingGlassIcon,
  StarIcon,
} from '@heroicons/react/24/outline';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const TradingPlatform = () => {
  const [selectedSymbol, setSelectedSymbol] = useState('AAPL');
  const [selectedTimeframe, setSelectedTimeframe] = useState('1D');
  const [orderType, setOrderType] = useState('market');
  const [orderSide, setOrderSide] = useState('buy');
  const [quantity, setQuantity] = useState('');
  const [price, setPrice] = useState('');

  // Mock market data
  const [marketData, setMarketData] = useState([
    { symbol: 'AAPL', name: 'Apple Inc.', price: 178.45, change: 2.34, changePercent: 1.33, volume: '45.2M' },
    { symbol: 'GOOGL', name: 'Alphabet Inc.', price: 2734.56, change: -12.45, changePercent: -0.45, volume: '1.2M' },
    { symbol: 'TSLA', name: 'Tesla Inc.', price: 234.67, change: 8.90, changePercent: 3.95, volume: '23.1M' },
    { symbol: 'MSFT', name: 'Microsoft Corp.', price: 345.78, change: 5.67, changePercent: 1.67, volume: '18.4M' },
    { symbol: 'BTC', name: 'Bitcoin', price: 45678.90, change: -1234.56, changePercent: -2.63, volume: '2.1B' },
    { symbol: 'ETH', name: 'Ethereum', price: 3234.56, change: 123.45, changePercent: 3.97, volume: '1.8B' },
  ]);

  const [portfolio, setPortfolio] = useState([
    { symbol: 'AAPL', quantity: 50, avgPrice: 165.20, currentPrice: 178.45, value: 8922.50 },
    { symbol: 'GOOGL', quantity: 5, avgPrice: 2650.00, currentPrice: 2734.56, value: 13672.80 },
    { symbol: 'BTC', quantity: 0.5, avgPrice: 42000.00, currentPrice: 45678.90, value: 22839.45 },
  ]);

  // Mock chart data
  const generateChartData = () => {
    const labels = [];
    const data = [];
    const basePrice = marketData.find(item => item.symbol === selectedSymbol)?.price || 100;
    
    for (let i = 0; i < 50; i++) {
      labels.push(`${i}`);
      data.push(basePrice + (Math.random() - 0.5) * 20);
    }
    
    return {
      labels,
      datasets: [
        {
          label: selectedSymbol,
          data,
          borderColor: 'rgb(59, 130, 246)',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          borderWidth: 2,
          fill: true,
          tension: 0.1,
        },
      ],
    };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        mode: 'index',
        intersect: false,
      },
    },
    scales: {
      x: {
        display: false,
      },
      y: {
        display: true,
        position: 'right',
      },
    },
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false,
    },
  };

  const orderBookData = {
    bids: [
      { price: 178.44, size: 1200, total: 1200 },
      { price: 178.43, size: 800, total: 2000 },
      { price: 178.42, size: 1500, total: 3500 },
      { price: 178.41, size: 900, total: 4400 },
      { price: 178.40, size: 2100, total: 6500 },
    ],
    asks: [
      { price: 178.45, size: 1000, total: 1000 },
      { price: 178.46, size: 750, total: 1750 },
      { price: 178.47, size: 1300, total: 3050 },
      { price: 178.48, size: 600, total: 3650 },
      { price: 178.49, size: 1800, total: 5450 },
    ],
  };

  const timeframes = ['1M', '5M', '15M', '1H', '4H', '1D', '1W'];

  const handleOrder = () => {
    console.log('Order placed:', { symbol: selectedSymbol, orderType, orderSide, quantity, price });
    // Order logic here
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white w-full overflow-x-hidden">
      {/* Header */}
      <div className="bg-gray-800 border-b border-gray-700 px-2 sm:px-4 py-3 w-full">
        <div className="flex items-center justify-between flex-wrap gap-2 max-w-full">
          <div className="flex items-center space-x-2 min-w-0 flex-1">
            <h1 className="text-base sm:text-lg md:text-xl font-bold truncate">Trading Platform</h1>
            <div className="hidden lg:flex items-center space-x-4">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search symbols..."
                  className="pl-10 pr-4 py-2 bg-gray-700 border border-gray-600 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 w-48"
                />
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-2 text-xs sm:text-sm flex-shrink-0">
            <div className="hidden sm:block">
              <span className="text-gray-400">Portfolio:</span>
              <span className="ml-1 font-semibold text-green-400">$45.4K</span>
            </div>
            <div>
              <span className="text-gray-400">P&L:</span>
              <span className="ml-1 font-semibold text-green-400">+$1.2K</span>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row min-h-0 w-full">
        {/* Left Sidebar - Market Watch */}
        <div className="w-full lg:w-80 bg-gray-800 border-b lg:border-b-0 lg:border-r border-gray-700 overflow-y-auto max-h-64 lg:max-h-screen flex-shrink-0">
          <div className="p-2 sm:p-4">
            <h2 className="text-base sm:text-lg font-semibold mb-4">Market Watch</h2>
            <div className="space-y-2">
              {marketData.map((item) => (
                <motion.div
                  key={item.symbol}
                  whileHover={{ backgroundColor: 'rgba(59, 130, 246, 0.1)' }}
                  onClick={() => setSelectedSymbol(item.symbol)}
                  className={`p-3 rounded-lg cursor-pointer transition-colors ${
                    selectedSymbol === item.symbol ? 'bg-primary-600/20 border border-primary-500' : 'hover:bg-gray-700'
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm">{item.symbol}</div>
                      <div className="text-xs text-gray-400 truncate">{item.name}</div>
                    </div>
                    <div className="text-right ml-2">
                      <div className="font-semibold text-sm">${item.price.toLocaleString()}</div>
                      <div className={`text-xs flex items-center justify-end ${
                        item.change >= 0 ? 'text-green-400' : 'text-red-400'
                      }`}>
                        {item.change >= 0 ? (
                          <ArrowUpIcon className="h-3 w-3 mr-1" />
                        ) : (
                          <ArrowDownIcon className="h-3 w-3 mr-1" />
                        )}
                        {item.changePercent.toFixed(2)}%
                      </div>
                    </div>
                  </div>
                  <div className="text-xs text-gray-400 mt-1 lg:block hidden">Vol: {item.volume}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
          {/* Chart Area */}
          <div className="flex-1 p-2 sm:p-4 min-h-0">
            <div className="bg-gray-800 rounded-lg h-full overflow-hidden">
              {/* Chart Header */}
              <div className="p-4 border-b border-gray-700">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4 min-w-0">
                    <h2 className="text-lg sm:text-xl font-bold truncate">{selectedSymbol}</h2>
                    <div className="text-xl sm:text-2xl font-bold">
                      ${marketData.find(item => item.symbol === selectedSymbol)?.price.toLocaleString()}
                    </div>
                    <div className={`flex items-center text-sm ${
                      (marketData.find(item => item.symbol === selectedSymbol)?.change || 0) >= 0 
                        ? 'text-green-400' : 'text-red-400'
                    }`}>
                      {(marketData.find(item => item.symbol === selectedSymbol)?.change || 0) >= 0 ? (
                        <ArrowUpIcon className="h-4 w-4 mr-1" />
                      ) : (
                        <ArrowDownIcon className="h-4 w-4 mr-1" />
                      )}
                      {marketData.find(item => item.symbol === selectedSymbol)?.changePercent.toFixed(2)}%
                    </div>
                  </div>
                  <div className="flex items-center space-x-1 sm:space-x-2 overflow-x-auto">
                    {timeframes.map((tf) => (
                      <button
                        key={tf}
                        onClick={() => setSelectedTimeframe(tf)}
                        className={`px-2 sm:px-3 py-1 rounded text-xs sm:text-sm transition-colors flex-shrink-0 ${
                          selectedTimeframe === tf 
                            ? 'bg-primary-600 text-white' 
                            : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                        }`}
                      >
                        {tf}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Chart */}
              <div className="p-4 h-96">
                <Line data={generateChartData()} options={chartOptions} />
              </div>
            </div>
          </div>
        </div>

        {/* Right Sidebar */}
        <div className="w-full lg:w-80 bg-gray-800 border-t lg:border-t-0 lg:border-l border-gray-700 flex flex-col flex-shrink-0 lg:max-h-screen overflow-y-auto">
          {/* Order Panel */}
          <div className="p-2 sm:p-4 border-b border-gray-700">
            <h3 className="text-lg font-semibold mb-4">Place Order</h3>
            
            {/* Order Type Tabs */}
            <div className="flex mb-4">
              <button
                onClick={() => setOrderSide('buy')}
                className={`flex-1 py-2 px-4 rounded-l-lg font-medium transition-colors ${
                  orderSide === 'buy' 
                    ? 'bg-green-600 text-white' 
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                Buy
              </button>
              <button
                onClick={() => setOrderSide('sell')}
                className={`flex-1 py-2 px-4 rounded-r-lg font-medium transition-colors ${
                  orderSide === 'sell' 
                    ? 'bg-red-600 text-white' 
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                Sell
              </button>
            </div>

            {/* Order Type */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">Order Type</label>
              <select
                value={orderType}
                onChange={(e) => setOrderType(e.target.value)}
                className="w-full p-2 bg-gray-700 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="market">Market</option>
                <option value="limit">Limit</option>
                <option value="stop">Stop</option>
                <option value="stop-limit">Stop Limit</option>
              </select>
            </div>

            {/* Quantity */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">Quantity</label>
              <input
                type="number"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                placeholder="0"
                className="w-full p-2 bg-gray-700 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>

            {/* Price (for limit orders) */}
            {orderType !== 'market' && (
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">Price</label>
                <input
                  type="number"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  placeholder="0.00"
                  className="w-full p-2 bg-gray-700 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            )}

            {/* Submit Button */}
            <button
              onClick={handleOrder}
              className={`w-full py-3 rounded-lg font-semibold transition-colors ${
                orderSide === 'buy'
                  ? 'bg-green-600 hover:bg-green-700 text-white'
                  : 'bg-red-600 hover:bg-red-700 text-white'
              }`}
            >
              {orderSide === 'buy' ? 'Buy' : 'Sell'} {selectedSymbol}
            </button>
          </div>

          {/* Order Book */}
          <div className="p-4 border-b border-gray-700">
            <h3 className="text-lg font-semibold mb-4">Order Book</h3>
            <div className="space-y-2">
              <div className="text-xs font-semibold text-gray-400 grid grid-cols-3 gap-2">
                <span>Price</span>
                <span>Size</span>
                <span>Total</span>
              </div>
              
              {/* Asks */}
              {orderBookData.asks.reverse().map((ask, index) => (
                <div key={`ask-${index}`} className="text-xs grid grid-cols-3 gap-2 text-red-400">
                  <span>{ask.price.toFixed(2)}</span>
                  <span>{ask.size.toLocaleString()}</span>
                  <span>{ask.total.toLocaleString()}</span>
                </div>
              ))}
              
              {/* Spread */}
              <div className="text-center text-xs text-gray-400 py-1 border-t border-b border-gray-600">
                Spread: $0.01
              </div>
              
              {/* Bids */}
              {orderBookData.bids.map((bid, index) => (
                <div key={`bid-${index}`} className="text-xs grid grid-cols-3 gap-2 text-green-400">
                  <span>{bid.price.toFixed(2)}</span>
                  <span>{bid.size.toLocaleString()}</span>
                  <span>{bid.total.toLocaleString()}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Portfolio */}
          <div className="flex-1 p-4 overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">Portfolio</h3>
            <div className="space-y-3">
              {portfolio.map((position, index) => (
                <div key={index} className="bg-gray-700 p-3 rounded-lg">
                  <div className="flex justify-between items-start mb-2">
                    <div className="font-semibold">{position.symbol}</div>
                    <div className="text-right">
                      <div className="text-sm font-semibold">${position.value.toLocaleString()}</div>
                      <div className={`text-xs ${
                        position.currentPrice > position.avgPrice ? 'text-green-400' : 'text-red-400'
                      }`}>
                        {((position.currentPrice - position.avgPrice) / position.avgPrice * 100).toFixed(2)}%
                      </div>
                    </div>
                  </div>
                  <div className="text-xs text-gray-400">
                    <div>Qty: {position.quantity}</div>
                    <div>Avg: ${position.avgPrice.toFixed(2)}</div>
                    <div>Current: ${position.currentPrice.toFixed(2)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TradingPlatform; 